import React, { useCallback, useEffect, useMemo, useState } from "react";
import ProspectingContactsTable from "./ProspectingContactsTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { isCancel } from "../../../services/api/axios";
import normalizeJobTitle from "../../../utils/jobTitleNormalizer";
import { addRecentItem } from "../../home-dashboard/RecentItemsHelper";
import { useAuth } from "../../../providers/AuthProvider";
import useRowSelection from "../../../hooks/useRowSelection";
import ContactsModuleSwitcher from "../../../shared/components/ContactsModuleSwitcher";

import {
  ACTION_MENU_ICON_SX,
  ACTION_MENU_ICON_TONES,
  ACTION_MENU_PAPER_SX,
  ACTIONS_HEADER_BTN_SX,
} from "../../../theme/buttonstyles";

import {
  dedupeProspectingContactsById,
  downloadBlobFile,
  normalizeProspectingContact,
} from "./ProspectingContactsHelpers";

import {
  Box,
  Button,
  Card,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import EntityProfile from "../../../shared/components/EntityProfile";
import DeleteConfirmDialog from "../../../shared/components/DeleteConfirmDialog";
import { contactProfileConfig } from "../../../shared/profile-configs/ContactProfileConfig";
import SearchBar from "../../../shared/components/SearchBar";
import BrandPagination from "../../../layouts/Pagination";
import {
  getUniqueCompanyNames,
  getUniqueJobTitles,
  sortContactsByColumn,
} from "../../../utils/contactListUtils";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const PAGE_BG = "#f7f9fc";

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  background: "#ffffff",
  overflow: "hidden",
};

export default function ProspectingContacts({
  embedded = false,
  prospectingCompanyId: forcedCompanyId,
  refreshKey = 0,
}) {
  const [contacts, setContacts] = useState([]);
  const { user } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");

  const [jobTitleFilter, setJobTitleFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");

  const [profileOpen, setProfileOpen] = useState(false);
  const [activeContact, setActiveContact] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const rowsPerPage = 25;

  const [orderBy, setOrderBy] = useState("name");
  const [orderDirection, setOrderDirection] = useState("asc");

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [searchParams] = useSearchParams();
  const prospectCompanyId = forcedCompanyId ?? searchParams.get("company_id");

  const navigate = useNavigate();
  const actionsMenuOpen = Boolean(actionsAnchorEl);

  /* ===============================
     FETCH CONTACTS
  =============================== */
  const fetchContacts = useCallback(
    async (signal) => {
      try {
        const res = await api.get("/prospecting-contacts", {
          signal,
          params: {
            company_id: prospectCompanyId || undefined,
          },
        });

        const rows = Array.isArray(res.data) ? res.data : [];
        setContacts(
          dedupeProspectingContactsById(rows.map(normalizeProspectingContact)),
        );
        return rows;
      } catch (err) {
        if (!isCancel(err)) {
          setSnackbarSeverity("error");
          setSnackbarMsg("Failed to load contacts.");
          setSnackbarOpen(true);
        }
        return [];
      }
    },
    [prospectCompanyId],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchContacts(controller.signal);
    return () => controller.abort();
  }, [fetchContacts, refreshKey]);

  /* ===============================
     FILTERING
  =============================== */
  const filteredContacts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return contacts.filter((c) => {
      if (
        companyFilter &&
        !c.company?.name?.toLowerCase().includes(companyFilter.toLowerCase())
      )
        return false;

      if (
        normalizedSearch &&
        !(
          c.name?.toLowerCase().includes(normalizedSearch) ||
          c.company?.name?.toLowerCase().includes(normalizedSearch) ||
          c.job_title?.toLowerCase().includes(normalizedSearch) ||
          c.email?.toLowerCase().includes(normalizedSearch)
        )
      )
        return false;

      if (jobTitleFilter && normalizeJobTitle(c.job_title) !== jobTitleFilter)
        return false;

      return true;
    });
  }, [contacts, companyFilter, searchTerm, jobTitleFilter]);

  const sortedContacts = useMemo(() => {
    return sortContactsByColumn({
      contacts: filteredContacts,
      orderBy,
      orderDirection,
      getValue: (contact, key) => {
        if (key === "company") return contact.company?.name;
        if (key === "job_title") return normalizeJobTitle(contact.job_title);
        return contact.name;
      },
    });
  }, [filteredContacts, orderBy, orderDirection]);

  const handleSortRequest = useCallback(
    (key) => {
      const isAsc = orderBy === key && orderDirection === "asc";
      setOrderBy(key);
      setOrderDirection(isAsc ? "desc" : "asc");
    },
    [orderBy, orderDirection],
  );

  useEffect(() => {
    setPage(1);
  }, [searchTerm, companyFilter, jobTitleFilter, prospectCompanyId]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedContacts.length / rowsPerPage),
  );
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * rowsPerPage;
  const paginatedContacts = sortedContacts.slice(start, start + rowsPerPage);

  const {
    selectedIds,
    setSelectedIds,
    selectedCount,
    toggleOne,
    togglePage,
    clear: clearSelection,
    allSelected,
    someSelected,
  } = useRowSelection(paginatedContacts, (contact) => contact.id);

  const hasContacts = paginatedContacts.length > 0;

  /* ===============================
     DELETE
  =============================== */
  const handleDeleteSelected = () => {
    if (!selectedIds.size) return;
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteSelected = async () => {
    const ids = Array.from(selectedIds);

    setDeleting(true);
    const results = await Promise.allSettled(
      ids.map((id) => api.delete(`/prospecting-contacts/${id}`)),
    );
    const succeeded = results.filter(
      (result) => result.status === "fulfilled",
    ).length;
    const failed = results.length - succeeded;
    const succeededIds = ids.filter(
      (_id, index) => results[index]?.status === "fulfilled",
    );

    setSnackbarSeverity(failed ? "warning" : "success");
    setSnackbarMsg(
      failed
        ? `Deleted ${succeeded}, but ${failed} failed.`
        : "Contacts deleted",
    );
    setSnackbarOpen(true);
    if (succeededIds.length > 0) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        succeededIds.forEach((id) => next.delete(String(id)));
        return next;
      });
    }
    setDeleting(false);
    setDeleteConfirmOpen(false);
    await fetchContacts();
  };

  const handleOpenActionsMenu = useCallback((event) => {
    setActionsAnchorEl(event.currentTarget);
  }, []);

  const handleCloseActionsMenu = useCallback(() => {
    setActionsAnchorEl(null);
  }, []);

  /* ===============================
     EXPORT
  =============================== */
  const handleExportContacts = () => {
    api
      .get("/prospecting-contacts/export", {
        responseType: "blob",
        params: {
          company_id: prospectCompanyId || undefined,
          search: searchTerm || undefined,
        },
      })
      .then((response) => {
        const cd = response.headers?.["content-disposition"];
        const match = cd && /filename="?([^"]+)"?/.exec(cd);
        downloadBlobFile(
          response.data,
          match?.[1] || "ProspectingContacts.xlsx",
        );
      })
      .catch(() => {
        setSnackbarSeverity("error");
        setSnackbarMsg("Export failed. Please try again.");
        setSnackbarOpen(true);
      });
  };

  const handleExportContactsFromMenu = () => {
    handleCloseActionsMenu();
    handleExportContacts();
  };

  const handleClearSelectionFromMenu = () => {
    handleCloseActionsMenu();
    clearSelection();
  };

  const handleDeleteSelectedFromMenu = async () => {
    handleCloseActionsMenu();
    await handleDeleteSelected();
  };

  const uniqueCompanyNames = useMemo(
    () => getUniqueCompanyNames(contacts),
    [contacts],
  );

  const uniqueJobTitles = useMemo(
    () => getUniqueJobTitles(contacts, normalizeJobTitle),
    [contacts],
  );

  /* ===============================
     RENDER
  =============================== */
  if (embedded) {
    return (
      <>
        <ProspectingContactsTable
          embedded={embedded}
          paginatedContacts={paginatedContacts}
          hasContacts={hasContacts}
          selectedIds={selectedIds}
          allSelected={allSelected}
          someSelected={someSelected}
          toggleOne={toggleOne}
          togglePage={togglePage}
          navigate={navigate}
          orderBy={orderBy}
          orderDirection={orderDirection}
          onSort={handleSortRequest}
          companyFilter={companyFilter}
          onCompanyFilterChange={setCompanyFilter}
          uniqueCompanyNames={uniqueCompanyNames}
          jobTitleFilter={jobTitleFilter}
          onJobTitleFilterChange={setJobTitleFilter}
          uniqueJobTitles={uniqueJobTitles}
          setActiveContact={setActiveContact}
          setProfileOpen={setProfileOpen}
          user={user}
          addRecentItem={addRecentItem}
        />
        {hasContacts && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <BrandPagination
              page={safePage}
              count={totalPages}
              onChange={(_e, val) => setPage(val)}
            />
          </Box>
        )}
      </>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: PAGE_BG,
        pb: 4,
      }}
    >
      {/* ===== Sticky Header ===== */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
            flexWrap: "wrap",
          }}
        >
          {/* Left: icon + title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: BRAND_GREEN,
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <PeopleIcon sx={{ fontSize: 19 }} />
            </Box>
            <ContactsModuleSwitcher current="prospecting" />
          </Box>

          {/* Center: search */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search Contact Name, Company or Job Title..."
              maxWidth={1700}
            />
          </Box>

          {/* Right: actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleOpenActionsMenu}
              sx={ACTIONS_HEADER_BTN_SX}
            >
              Actions
            </Button>

            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsMenuOpen}
              onClose={handleCloseActionsMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
            >
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  navigate(
                    prospectCompanyId
                      ? `/prospecting-contacts/new?company_id=${prospectCompanyId}`
                      : "/prospecting-contacts/new",
                  );
                }}
              >
                <ListItemIcon
                  sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.add }}
                >
                  <PersonAddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Prospecting Contact</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={handleExportContactsFromMenu}
                disabled={!contacts.length}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.export,
                  }}
                >
                  <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={handleClearSelectionFromMenu}
                disabled={!selectedCount}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.clear,
                  }}
                >
                  <ClearAllIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Clear Selection</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={handleDeleteSelectedFromMenu}
                disabled={!selectedCount}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.delete,
                  }}
                >
                  <DeleteForeverIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{`Delete Selected (${selectedCount})`}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* ===== Content Area ===== */}
      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        <Card sx={CARD_SX}>
          <ProspectingContactsTable
            embedded={embedded}
            paginatedContacts={paginatedContacts}
            hasContacts={hasContacts}
            selectedIds={selectedIds}
            allSelected={allSelected}
            someSelected={someSelected}
            toggleOne={toggleOne}
            togglePage={togglePage}
            navigate={navigate}
            orderBy={orderBy}
            orderDirection={orderDirection}
            onSort={handleSortRequest}
            companyFilter={companyFilter}
            onCompanyFilterChange={setCompanyFilter}
            uniqueCompanyNames={uniqueCompanyNames}
            jobTitleFilter={jobTitleFilter}
            onJobTitleFilterChange={setJobTitleFilter}
            uniqueJobTitles={uniqueJobTitles}
            setActiveContact={setActiveContact}
            setProfileOpen={setProfileOpen}
            user={user}
            addRecentItem={addRecentItem}
          />
        </Card>
        {hasContacts && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <BrandPagination
              page={safePage}
              count={totalPages}
              onChange={(_e, val) => setPage(val)}
            />
          </Box>
        )}
      </Box>

      <EntityProfile
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        entity={activeContact}
        sections={activeContact ? contactProfileConfig(activeContact) : []}
        dialogPaperSx={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "#00336C",
          border: "1px solid #00336C",
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity={snackbarSeverity}>{snackbarMsg}</Alert>
      </Snackbar>

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteSelected}
        loading={deleting}
        title="Delete Contacts"
        description={`Are you sure you want to delete ${selectedIds.size} prospecting contact${selectedIds.size === 1 ? "" : "s"}? This action can't be undone.`}
      />
    </Box>
  );
}
